// A compact knowledge base of Nostr event kinds, used to help the AI
// "figure out what each kind does" on demand, and to drive the generic renderer.

export type KindStorage = "regular" | "replaceable" | "addressable" | "ephemeral" | "legacy";

export interface KindInfo {
  kind: number;
  /** Short human name. */
  name: string;
  /** One-line description of what the kind represents. */
  description: string;
  /** The NIP that defines it, if known. */
  nip?: string;
  /** Storage class. */
  storage: KindStorage;
  /** Hints for rendering. */
  render?: {
    /** Treat `content` as plaintext note-like content (linkify, media, mentions). */
    note?: boolean;
    /** `content` is JSON (stringified). */
    json?: boolean;
    /** This is a profile metadata event (kind 0). */
    profile?: boolean;
    /** Title tag to surface, e.g. "title", "name". */
    titleTag?: string;
    /** Summary tag, e.g. "summary", "about". */
    summaryTag?: string;
    /** Image tag, e.g. "image", "picture". */
    imageTag?: string;
  };
}

function classify(kind: number): KindStorage {
  if (kind >= 30000 && kind < 40000) return "addressable";
  if (kind >= 20000 && kind < 30000) return "ephemeral";
  if (kind >= 10000 && kind < 20000) return "replaceable";
  if (kind >= 1000 && kind < 10000) return "regular";
  return "legacy";
}

const KINDS: KindInfo[] = [
  { kind: 0, name: "Profile Metadata", description: "User profile: name, about, picture, banner, nip05, lud16.", nip: "NIP-01", storage: "replaceable", render: { profile: true } },
  { kind: 1, name: "Short Text Note", description: "A microblog post / tweet-like note.", nip: "NIP-01", storage: "regular", render: { note: true } },
  { kind: 3, name: "Follow List", description: "The user's contact/follow list (p-tags).", nip: "NIP-02", storage: "replaceable" },
  { kind: 4, name: "Encrypted DM (legacy)", description: "Legacy NIP-04 encrypted direct message.", nip: "NIP-04", storage: "regular" },
  { kind: 5, name: "Event Deletion", description: "Request to delete referenced events.", nip: "NIP-09", storage: "regular" },
  { kind: 6, name: "Repost", description: "A repost (boost) of a kind 1 note.", nip: "NIP-18", storage: "regular" },
  { kind: 7, name: "Reaction", description: "A like/emoji reaction to an event.", nip: "NIP-25", storage: "regular" },
  { kind: 8, name: "Badge Award", description: "Awards a badge to a user.", nip: "NIP-58", storage: "regular" },
  { kind: 9, name: "Group Chat Message", description: "A message in a NIP-29 relay-based group.", nip: "NIP-29", storage: "regular", render: { note: true } },
  { kind: 16, name: "Generic Repost", description: "A repost of a non-kind-1 event.", nip: "NIP-18", storage: "regular" },
  { kind: 1063, name: "File Metadata", description: "Describes a file (url, mime, hash).", nip: "NIP-94", storage: "regular" },
  { kind: 1111, name: "Comment", description: "A threaded comment on any event or external item.", nip: "NIP-22", storage: "regular", render: { note: true } },
  { kind: 1311, name: "Live Chat Message", description: "Chat message inside a live event.", nip: "NIP-53", storage: "regular", render: { note: true } },
  { kind: 1984, name: "Report", description: "Reports an event or user for moderation.", nip: "NIP-56", storage: "regular" },
  { kind: 9734, name: "Zap Request", description: "Request to a lightning service to zap an event.", nip: "NIP-57", storage: "regular" },
  { kind: 9735, name: "Zap Receipt", description: "Receipt proving a lightning zap was paid.", nip: "NIP-57", storage: "regular" },
  { kind: 10000, name: "Mute List", description: "Muted pubkeys, words, threads.", nip: "NIP-51", storage: "replaceable" },
  { kind: 10002, name: "Relay List", description: "The user's read/write relays (NIP-65).", nip: "NIP-65", storage: "replaceable" },
  { kind: 10063, name: "Blossom Servers", description: "User's preferred media (Blossom) servers.", nip: "BUD-03", storage: "replaceable" },
  { kind: 30000, name: "Follow Set", description: "A named, categorized set of follows.", nip: "NIP-51", storage: "addressable", render: { titleTag: "title" } },
  { kind: 30008, name: "Profile Badges", description: "Badges the user chose to display.", nip: "NIP-58", storage: "addressable" },
  { kind: 30009, name: "Badge Definition", description: "Defines a badge: name, image, description.", nip: "NIP-58", storage: "addressable", render: { titleTag: "name", imageTag: "image" } },
  { kind: 30023, name: "Long-form Article", description: "Markdown long-form article (blog post).", nip: "NIP-23", storage: "addressable", render: { titleTag: "title", summaryTag: "summary", imageTag: "image" } },
  { kind: 30024, name: "Draft Article", description: "An unpublished draft long-form article.", nip: "NIP-23", storage: "addressable", render: { titleTag: "title", summaryTag: "summary", imageTag: "image" } },
  { kind: 30030, name: "Emoji Set", description: "A set of custom emoji.", nip: "NIP-30", storage: "addressable", render: { titleTag: "title" } },
  { kind: 30311, name: "Live Event", description: "A live streaming event with status and participants.", nip: "NIP-53", storage: "addressable", render: { titleTag: "title", summaryTag: "summary", imageTag: "image" } },
  { kind: 31922, name: "Date-based Calendar Event", description: "An all-day calendar event.", nip: "NIP-52", storage: "addressable", render: { titleTag: "title", summaryTag: "summary" } },
  { kind: 31923, name: "Time-based Calendar Event", description: "A timed calendar event.", nip: "NIP-52", storage: "addressable", render: { titleTag: "title", summaryTag: "summary" } },
  { kind: 31924, name: "Calendar", description: "A calendar referencing calendar events.", nip: "NIP-52", storage: "addressable", render: { titleTag: "title" } },
  { kind: 31989, name: "App Recommendation", description: "Recommends an app for a given kind.", nip: "NIP-89", storage: "addressable" },
  { kind: 31990, name: "App Handler", description: "Declares an app that handles certain kinds.", nip: "NIP-89", storage: "addressable", render: { json: true } },
  { kind: 34550, name: "Community Definition", description: "Defines a NIP-72 moderated community.", nip: "NIP-72", storage: "addressable", render: { titleTag: "name", summaryTag: "description", imageTag: "image" } },
];

const BY_KIND = new Map<number, KindInfo>(KINDS.map((k) => [k.kind, k]));

/** Look up rich info about a kind, synthesizing a fallback for unknown kinds. */
export function getKindInfo(kind: number): KindInfo {
  const known = BY_KIND.get(kind);
  if (known) return known;
  return {
    kind,
    name: `Kind ${kind}`,
    description:
      "An event kind not in the local knowledge base. Behavior is inferred from its tags and storage class.",
    storage: classify(kind),
    render: {
      // Heuristic: regular text-bearing kinds often hold note-like content.
      note: kind === 1 || (kind > 1 && kind < 45),
    },
  };
}

/** The full list, for the AI to browse. */
export function listKnownKinds(): KindInfo[] {
  return KINDS;
}

export function storageClass(kind: number): KindStorage {
  return classify(kind);
}
